// richPaste.js
// Lets users copy/paste directly from Microsoft Word, Microsoft Excel and
// images into the editor.
//
//  - WORD  : pastes as HTML on the clipboard. We strip Office cruft
//            (mso- styles, <o:p>, conditional comments, empty spans) but keep
//            headings, bold/italic, lists, links and tables.
//  - EXCEL : also pastes as HTML — a <table> with mso styles. Same cleaner
//            turns it into a clean <table> the editor + Word export understand.
//            (Plain-text fallback: tab/newline separated cells -> table.)
//  - IMAGE : arrives as a clipboard file (screenshot) or an <img src> in HTML.
//            We read it as a data URL (or upload it) and insert an image node.
 
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
 
// Strip Microsoft Office–specific markup while preserving real structure.
export function cleanOfficeHtml(html) {
  if (!html) return '';
  let s = html;
  // Remove Word's conditional comments <!--[if ...]> ... <![endif]-->
  s = s.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '');
  s = s.replace(/<!--[\s\S]*?-->/g, '');
  // Drop <o:p>, <xml>, <style>, <meta>, <link> blocks
  s = s.replace(/<\/?o:p>/gi, '');
  s = s.replace(/<xml[\s\S]*?<\/xml>/gi, '');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '');
  s = s.replace(/<(meta|link)[^>]*>/gi, '');
  // Remove mso-* inline styles and class="Mso..." attributes
  s = s.replace(/\s*mso-[^:;"]+:[^;"]+;?/gi, '');
  s = s.replace(/\sclass="?Mso[^"\s>]*"?/gi, '');
  s = s.replace(/\sstyle="\s*"/gi, '');
  // Strip lang= and Word's span wrappers that now carry nothing
  s = s.replace(/\slang="[^"]*"/gi, '');
  // Collapse empty spans
  s = s.replace(/<span[^>]*>(\s*)<\/span>/gi, '$1');
  // Tag Excel/Word tables with our class so they get DRHP table styling
  s = s.replace(/<table/gi, '<table class="drhp-table"');
  return s.trim();
}
 
// Plain-text grid (TSV from Excel) -> HTML table.
function tsvToTable(text) {
  const rows = text.replace(/\r/g, '').split('\n').filter((r) => r.length);
  if (rows.length < 2 || !rows.some((r) => r.includes('\t'))) return null;
  const cells = rows.map((r) => r.split('\t'));
  const body = cells.map((row, i) => {
    const tag = i === 0 ? 'th' : 'td';
    return '<tr>' + row.map((c) =>
      `<${tag}>${c.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</${tag}>`).join('') + '</tr>';
  }).join('');
  return `<table class="drhp-table">${body}</table>`;
}
 
const richPasteKey = new PluginKey('richPaste');
 
// uploadImage(file) -> Promise<string url>. Default returns a data URL so the
// editor works with zero backend; swap for a real upload in production.
export const RichPaste = Extension.create({
  name: 'richPaste',
  addOptions() {
    return { uploadImage: async (file) => await fileToDataUrl(file) };
  },
  addProseMirrorPlugins() {
    const { uploadImage } = this.options;
    const editor = this.editor;
 
    return [new Plugin({
      key: richPasteKey,
      props: {
        handlePaste(view, event) {
          const cd = event.clipboardData;
          if (!cd) return false;
 
          // 1) Pasted image files (e.g. screenshot, or copy image from Word)
          const imageItems = Array.from(cd.items || [])
            .filter((it) => it.type && it.type.startsWith('image/'));
          if (imageItems.length) {
            event.preventDefault();
            imageItems.forEach((it) => {
              const file = it.getAsFile();
              if (!file) return;
              uploadImage(file).then((url) => {
                editor.chain().focus().setImage({ src: url }).run();
              });
            });
            return true;
          }
 
          // 2) HTML from Word / Excel
          const html = cd.getData('text/html');
          if (html && /<\/?(table|p|h[1-6]|ul|ol|li|span|div|img)/i.test(html)) {
            const cleaned = cleanOfficeHtml(html);
            if (cleaned) {
              event.preventDefault();
              editor.chain().focus().insertContent(cleaned).run();
              return true;
            }
          }
 
          // 3) Plain-text grid from Excel -> table
          const text = cd.getData('text/plain');
          if (text && text.includes('\t')) {
            const tbl = tsvToTable(text);
            if (tbl) {
              event.preventDefault();
              editor.chain().focus().insertContent(tbl).run();
              return true;
            }
          }
 
          return false; // let the editor handle normal text
        },
 
        // Drag-and-drop image files
        handleDrop(view, event) {
          const files = Array.from(event.dataTransfer?.files || [])
            .filter((f) => f.type.startsWith('image/'));
          if (!files.length) return false;
          event.preventDefault();
          files.forEach((file) =>
            uploadImage(file).then((url) =>
              editor.chain().focus().setImage({ src: url }).run()));
          return true;
        },
      },
    })];
  },
});
 
function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
 